import "vendors/modernizr";
import { useFeatureFlag } from "common/feature_gating";
import { IA_NAV, ORGANIZATION_BRAND_NAME, PROOF_TRANSACTIONS } from "constants/feature_gates";
import { isNotaryNST } from "common/notary/capacity";

declare global {
  interface Window {
    Modernizr: { [key: string]: boolean };
  }
}

// Any changes to supported features should be marked in the Confluence documentation:
// https://notarize.atlassian.net/wiki/spaces/RSMB/pages/305070180/Supported+Browsers
const featureSets = {
  FLEXBOX: window.Modernizr.flexbox,
  FETCH: window.Modernizr.fetch,
  LOCALSTORAGE: window.Modernizr.localstorage,
  PDF: window.Modernizr.canvas && window.Modernizr.eventlistener && window.Modernizr.filereader,
  WEBRTC: window.Modernizr.peerconnection && window.Modernizr.getusermedia,
};

export const allFeatureSets = Boolean(
  featureSets.FLEXBOX &&
    featureSets.FETCH &&
    featureSets.LOCALSTORAGE &&
    featureSets.PDF &&
    featureSets.WEBRTC,
);

export const pdfFeatures = Boolean(featureSets.FLEXBOX && featureSets.FETCH && featureSets.PDF);

export const authenticationFeatures = Boolean(
  featureSets.FLEXBOX && featureSets.FETCH && featureSets.LOCALSTORAGE,
);

// Remove in REAL-8187
export function useIAnav() {
  return useFeatureFlag(IA_NAV);
}
export function useIAPreLaunchBanner() {
  return useFeatureFlag("ia-prelaunch-banner");
}
export function useIALaunchBanner() {
  return useFeatureFlag("ia-launch-banner");
}

export function useResponsiveSettings() {
  return useFeatureFlag("responsive-user-settings");
}

export function useConsistentLogin() {
  return useFeatureFlag("consistent-login", true);
}

export function useRebrandOrgBanner() {
  return useFeatureFlag("rebrand-org-banner");
}

export function useProofPhase2OrgBanner() {
  return useFeatureFlag("proof-phase-2-org-banner");
}

export function useProofPhase3OrgBanner() {
  return useFeatureFlag("proof-phase-3-org-banner");
}

export function useRebrandLoginBanner() {
  return useFeatureFlag("rebrand-login-banner");
}

export function usePhase2RebrandLoginBanner() {
  return useFeatureFlag("proof-phase-2-login-banner");
}

export function usePhase3RebrandLoginBanner() {
  return useFeatureFlag("proof-phase-3-login-banner");
}

export const useProofRebrand = () => {
  // flag exists in launch darkly for both signer and business
  // https://notarize.atlassian.net/wiki/spaces/MRT/pages/3219095860/Rebrand+branching+logic
  return useFeatureFlag("proof-rebrand", false);
};

export function useCompanyName() {
  return useProofRebrand() ? "Proof" : "Notarize";
}

export function useProofTransactionsEnabled(notaryProfile: Parameters<typeof isNotaryNST>[0]) {
  return useFeatureFlag(PROOF_TRANSACTIONS) && !isNotaryNST(notaryProfile);
}

export function useOrganizationBrandName() {
  return useFeatureFlag(ORGANIZATION_BRAND_NAME);
}
